import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Typography,
  InputLabel,
} from '@mui/material';
import React, { useState } from 'react';
import { useBookingsContext } from '../../../../contexts/BookingsContext';
import { BookingStatus, IBookingsFilter } from '../../../../global/interfaces';
import useBookings from '../../../../hooks/useBookings';
import toggleToast from '../../../../utils/messaging';
import BookingModal from '../../../../components/BookingModal';
import BookSession from '../../../../components/BookSession';
import CustomDateSelector from '../../CustomDatePicker';
import RoundedInput from '../../../../components/RoundedInput';
import MiniCalendar from '../../MiniCalendar';
import { useCalendarStyles } from './styles';
import { COLORS } from '../../../../utils/colors';

interface DateRangeProps {
  setStartDate: (day: Date | null) => void;
  setEndDate: (day: Date | null) => void;
  startDate: Date | null;
  endDate: Date | null;
}

interface sideMenuProps {
  setOpenFilters: (open: boolean) => void;
  openFilters: boolean;
  filters: IBookingsFilter;
  setFilters: (filters: IBookingsFilter) => void;
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  setCalView: (view: boolean) => void;
  isReportsPage: boolean;
}

export const options = [
  { label: 'Problem Solver', color: '#f37720', value: 1 },
  { label: 'Home Health Check', color: '#1d88dc', value: 5 },
  { label: 'Home 101', color: '#16ab3b', value: 6 },
  { label: 'On Site', color: '#7c2da3', value: 8 },
];

const DateRangePicker = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
}: DateRangeProps) => {
  return (
    <Box display="flex" alignItems="center">
      <Box paddingRight={'0.5rem'}>
        <CustomDateSelector
          setSelectedDate={setStartDate}
          selectedDate={startDate}
          onDateChange={(date: Date) => {
            if (!endDate || date <= endDate) {
              setStartDate(date);
            } else {
              setStartDate(null);
              toggleToast('Start date cannot be after the end date.', 'error');
            }
          }}
          minDate={null}
          maxDate={endDate || null}
        />
      </Box>
      -
      <Box paddingLeft={'0.5rem'}>
        <CustomDateSelector
          setSelectedDate={setEndDate}
          selectedDate={endDate}
          onDateChange={(date: Date) => {
            if (!startDate || date >= startDate) {
              setEndDate(date);
            } else {
              setEndDate(null);
              toggleToast('End date cannot be before the start date.', 'error');
            }
          }}
          minDate={startDate || null}
          maxDate={null}
        />
      </Box>
    </Box>
  );
};

const CalendarSideMenu = ({
  setOpenFilters,
  currentDate,
  setCurrentDate,
  setCalView,
  isReportsPage,
}: sideMenuProps) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [bookingModalOpen, setBookingCalendarModalOpen] = useState(false);
  const styles = useCalendarStyles();
  const [searchValue, setSearchValue] = useState('');
  const { filters, setFilters, setBookingsFilter } = useBookingsContext();

  const [checked, setChecked] = useState<{ label: string; value: number }[]>(
    [],
  );
  const [checkedPromo, setCheckedPromo] = useState(false);
  const [region, setRegion] = useState('');
  const [status, setStatus] = useState<BookingStatus>(-1);
  const bookingStatuses: any = [
    { label: 'All', value: -1 },
    { label: 'Pending', value: 0 },
    { label: 'Open', value: 1 },
    { label: 'Session started', value: 2 },
    { label: 'Session completed', value: 3 },
    { label: 'Session canceled', value: 4 },
    { label: 'Report started', value: 5 },
    { label: 'Report uploaded', value: 6 },
    { label: 'Report sent', value: 7 },
  ];

  const handleToggle = (label: string, value: number) => {
    const newEntry = { label, value };
    const currentIndex = checked.findIndex(
      (item) => item.label === label && item.value === value,
    );
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(newEntry);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const resetFilterContent = () => {
    setStartDate(null);
    setEndDate(null);
    setSearchValue('');
    setChecked([]);
    setCheckedPromo(false);
    setRegion('');
    setStatus(-1);
  };

  const updatePromoCodeUsed = () => {
    setCheckedPromo(!checkedPromo);
  };

  const filterSchedule = () => {
    if (!startDate || !endDate) {
      toggleToast('You must provide a date range', 'error');
      return;
    }
    setCalView(false);
    if (filters) {
      const sessionTypeLabels = checked.map((item) => item.label);
      const sessionTypeValue = checked.map((item) => item.value);

      const statusLabel =
        bookingStatuses.find((item: any) => item.value === status)?.label || '';

      const payload: IBookingsFilter = {
        search: searchValue,
        sessionType: sessionTypeValue,
        startDate: startDate ? startDate : undefined,
        endDate: endDate ? endDate : undefined,
        region: region,
        status: statusLabel === 'All' ? undefined : status,
        promoCode: checkedPromo,
      };
      setOpenFilters(true);
      setFilters({
        search: searchValue,
        sessionType: sessionTypeLabels,
        startDate: startDate ? startDate : undefined,
        endDate: endDate ? endDate : undefined,
        region: region,
        status: statusLabel === 'All' ? undefined : statusLabel,
        promoCode: checkedPromo,
      });
      setBookingsFilter(payload);
    } else {
      toggleToast('No Filters Selected', 'error');
    }
  };

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setStatus(event.target.value as BookingStatus);
  };

  const disabled =
    startDate && endDate && (startDate > endDate || endDate < startDate);

  return (
    <>
      <Box sx={styles.sideMenuContainer}>
        <Box sx={styles.bookSession}>
          <Button
            onClick={() => setBookingCalendarModalOpen(true)}
            sx={styles.bookSessionButton}>
            + Book a session
          </Button>
        </Box>
        {!isReportsPage && (
          <Box>
            <MiniCalendar
              datePicker={false}
              currentDay={currentDate}
              onDateChange={(date: Date) => setCurrentDate(date)}
            />
          </Box>
        )}
        <Box sx={styles.headingContainer}>
          <Typography sx={styles.heading}>Filters</Typography>
          <RoundedInput
            sx={{
              border: 'solid 1px #464e5f',
              borderRadius: '2rem',
              '.MuiOutlinedInput-root fieldset': {
                border: 'none',
              },
            }}
            label="Search"
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}></RoundedInput>
        </Box>
        <Box>
          {!isReportsPage ? (
            <Accordion disableGutters sx={styles.accordianStyles}>
              <AccordionSummary
                sx={styles.accordianSummaryStyles}
                expandIcon={<ExpandMore />}>
                Session Type
              </AccordionSummary>
              <AccordionDetails sx={{ margin: '0px' }}>
                <List>
                  {options.map((option) => (
                    <ListItem
                      sx={{
                        fontSize: '0.8rem',
                        '& .MuiCheckbox-root': {
                          color: option.color,
                        },
                      }}
                      key={option.label}
                      disablePadding>
                      <Checkbox
                        size="small"
                        edge="start"
                        checked={
                          !!checked.find(
                            (item) =>
                              item.label === option.label &&
                              item.value === option.value,
                          )
                        }
                        tabIndex={-1}
                        disableRipple
                        onChange={() =>
                          handleToggle(option.label, option.value)
                        }
                      />
                      <ListItemText primary={option.label} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ) : (
            <List sx={{ paddingLeft: '1rem' }}>
              {options.map((option) => (
                <ListItem
                  sx={{
                    fontSize: '0.8rem',
                    '& .MuiCheckbox-root': {
                      color: option.color,
                    },
                  }}
                  key={option.label}
                  disablePadding>
                  <Checkbox
                    size="small"
                    edge="start"
                    checked={
                      !!checked.find(
                        (item) =>
                          item.label === option.label &&
                          item.value === option.value,
                      )
                    }
                    tabIndex={-1}
                    disableRipple
                    onChange={() => handleToggle(option.label, option.value)}
                  />
                  <ListItemText primary={option.label} />
                </ListItem>
              ))}
            </List>
          )}
          <Box>
            <Box sx={styles.dateRangeContainer}>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
              />
            </Box>
            <Box sx={{ marginBottom: '1rem' }}>
              <RoundedInput
                sx={{
                  border: 'solid 1px #464e5f',
                  borderRadius: '1.3rem',
                  '.MuiOutlinedInput-root fieldset': {
                    border: 'none',
                  },
                }}
                onChange={(e) => setRegion(e.target.value)}
                label={'City, Province'}
                size="small"
                value={region}></RoundedInput>
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel
                sx={{
                  fontSize: '0.7rem',
                  backgroundColor: 'white',
                  px: 1,
                  transform: 'translate(14px, -6px)',
                }}
                shrink>
                Status
              </InputLabel>
              <Select
                fullWidth
                size="small"
                sx={styles.statusSelect}
                value={status}
                onChange={(e: any) => handleChange(e)}>
                {bookingStatuses.map((status: any) => (
                  <MenuItem key={status} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Box marginBottom={'1rem'} marginLeft={'0.4rem'}>
          <FormControlLabel
            control={
              <Switch
                sx={{
                  fontSize: '13px',
                  '& .MuiSwitch-thumb': {
                    color: checkedPromo ? COLORS.PRIMARY : '#464e5f',
                  },
                }}
                checked={checkedPromo}
                onChange={updatePromoCodeUsed}
              />
            }
            label="Use of promo code"
          />
        </Box>
        <Box sx={styles.buttonContainer}>
          <Button
            onClick={() => {
              setFilters({});
              resetFilterContent();
            }}
            sx={styles.clearButton}>
            Clear
          </Button>
          <Button
            disabled={isReportsPage}
            onClick={() => filterSchedule()}
            sx={{
              ...styles.filterButton,
              opacity: disabled ? '0.8' : '1',
              color: disabled ? 'white' : 'white',
            }}>
            Filter
          </Button>
        </Box>
      </Box>
      <BookSession
        open={bookingModalOpen}
        onClose={() => setBookingCalendarModalOpen(false)}
      />
    </>
  );
};

export default CalendarSideMenu;
